import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CheckCircle2, AlertTriangle, Wrench, Phone } from "lucide-react";

const InstallationGuide = () => {
  const steps = [
    {
      title: "Preparation",
      items: [
        "Ensure the wall opening is level and square",
        "Clean the surface of any dust, debris, or loose material",
        "Measure the opening accurately - allow 2-3mm clearance each side",
        "Have silicone sealant ready (colour matched to your sill)"
      ]
    },
    {
      title: "Dry Fit",
      items: [
        "Place the sill in position without adhesive first",
        "Check for level using a spirit level",
        "Ensure adequate support underneath - sills should not span unsupported gaps over 600mm",
        "Mark the position if needed"
      ]
    },
    {
      title: "Installation",
      items: [
        "Apply a continuous bead of silicone to the underside edges",
        "For longer sills, apply additional silicone in the centre",
        "Carefully lower the sill into position",
        "Press firmly and check level again",
        "Wipe away any excess silicone immediately"
      ]
    },
    {
      title: "Finishing",
      items: [
        "Apply silicone sealant around all edges where the sill meets the wall",
        "Tool the silicone for a neat finish",
        "Allow 24 hours for the silicone to fully cure",
        "Avoid placing heavy objects on the sill during curing"
      ]
    }
  ];

  const warnings = [
    "Always wear appropriate PPE when handling stone",
    "Quartz is heavy - get assistance for sills over 1 metre",
    "Do not drop or knock edges - quartz can chip",
    "Avoid dragging sills across surfaces"
  ];

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      {/* Navigation */}
      <nav className="nav-wrapper" data-testid="install-nav">
        <div className="container-padding py-4 flex items-center justify-between max-w-7xl mx-auto">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#0F3D2E] flex items-center justify-center">
              <span className="text-white font-semibold text-lg">S</span>
            </div>
            <span className="font-semibold text-lg tracking-tight">
              Stone Sills Direct
            </span>
          </Link>

          <Link
            to="/"
            className="flex items-center gap-2 text-sm text-[#57534E] hover:text-[#0F3D2E]"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container-padding max-w-4xl mx-auto py-12" data-testid="installation-guide">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-12">
            <span className="section-caption text-[#C27C2E] mb-2 block">
              DIY Guide
            </span>
            <h1 className="text-3xl md:text-4xl font-medium mb-4">
              Installation Instructions
            </h1>
            <p className="text-lg text-[#57534E]">
              Follow these steps for a professional finish when installing your stone window sills.
            </p>
          </div>

          {/* Warning Box */}
          <div className="bg-amber-50 border border-amber-200 p-6 mb-12" data-testid="warnings">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-medium text-amber-900 mb-3">Important Safety Information</h3>
                <ul className="space-y-2">
                  {warnings.map((warning, index) => (
                    <li key={index} className="flex items-start gap-2 text-amber-800">
                      <span className="text-amber-600">•</span>
                      {warning}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Steps */}
          <div className="space-y-8">
            {steps.map((step, stepIndex) => (
              <motion.div
                key={step.title}
                className="stone-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: stepIndex * 0.1 }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-10 h-10 bg-[#0F3D2E] flex items-center justify-center text-white font-medium">
                    {stepIndex + 1}
                  </div>
                  <h2 className="text-xl font-medium">{step.title}</h2>
                </div>
                <ul className="space-y-3 ml-14">
                  {step.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#0F3D2E] flex-shrink-0 mt-0.5" />
                      <span className="text-[#57534E]">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Professional Installation */}
          <div className="mt-12 p-8 bg-[#0F3D2E]" data-testid="pro-install">
            <div className="flex items-start gap-4">
              <Wrench className="w-8 h-8 text-white flex-shrink-0" />
              <div>
                <h3 className="text-xl font-medium mb-2 text-white">Need Professional Installation?</h3>
                <p className="text-white/80 mb-4">
                  For larger orders or complex installations, we offer a professional fitting service. 
                  Contact us to discuss your project requirements and check availability in your area.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/calculator">
                    <Button className="bg-white text-[#0F3D2E] hover:bg-white/90">
                      Get a Quote
                    </Button>
                  </Link>
                  <a href="mailto:worktops@setinstonedidcot.co.uk">
                    <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">
                      <Phone className="w-4 h-4 mr-2" />
                      Contact Us
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Tools Needed */}
          <div className="mt-12" data-testid="tools-needed">
            <h2 className="text-2xl font-medium mb-6">Tools & Materials Needed</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Spirit level",
                "Tape measure",
                "Silicone sealant (colour matched)",
                "Silicone gun",
                "Clean cloths",
                "Masking tape (optional)",
                "Wooden wedges or packers",
                "Safety gloves"
              ].map((tool, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-white border border-[#E7E5E4]">
                  <CheckCircle2 className="w-4 h-4 text-[#0F3D2E]" />
                  <span className="text-[#57534E]">{tool}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default InstallationGuide;
